import { useLastNonNull } from "@lib/Hooks"
import { Button, ButtonType, ButtonTheme } from "@ui/common/buttons/Button"
import {
    Modal,
    ModalBody,
    ModalButtons,
    ModalContent,
    ModalHeader,
    ModalHeaderTitle,
} from "@ui/common/Modal"
import React from "react"
import { ServiceItemForm } from "@model/serviceRequests/ServiceItemForm"
import { ImageUrls } from "@ui/common/images/ImageUrls"
import { WorkRequestType } from "@model/workRequests/WorkRequest"

export function ServiceItemDeleteConfirmModal({
    serviceItem,
    onCancel,
    onConfirm,
}: {
    serviceItem: ServiceItemForm | null
    onCancel: () => void
    onConfirm: (serviceItem: ServiceItemForm) => void
}): JSX.Element {
    const data = useLastNonNull(serviceItem)
    if (data === null) return <Modal isVisible={serviceItem !== null} />

    const isNotSavedServiceItem = serviceItem?.workRequestType == WorkRequestType.Service && !serviceItem?.id

    function labelForServiceItemForm(request: ServiceItemForm): string {
        const label = request.name
        if (!label) return ""
        return label.length > 0 ? label : `${request.id}`
    }

    return (
        <Modal isVisible={serviceItem !== null} useContentTag={true} onClose={onCancel}>
            <ModalContent>
                <div className="service-item-delete-modal">
                    <ModalHeader>
                        <ModalHeaderTitle>
                            {isNotSavedServiceItem ?
                                <>
                                    <img src={ImageUrls.Trash} alt="Delete" className="delete-icon" />
                                    Delete Service Item
                                </> :

                                <>
                                    Decline Service Item
                                </>
                            }
                        </ModalHeaderTitle>
                    </ModalHeader>
                    <ModalBody>
                        {isNotSavedServiceItem ?
                            <>
                                <div className="delete-confirm-message">
                                    Are you sure you want to {isNotSavedServiceItem ?
                                        "delete " : "decline "}
                                    <strong>{data.name || `this service item`}</strong>?
                                </div>
                            </>
                            :
                            <>
                                <div className="delete-confirm-message">
                                    Are you sure you wish to decline this service item from this work order? Declining a service item will return it to the queue of service items.
                                </div>
                                <i>{labelForServiceItemForm(data)}</i>
                            </>
                        }
                        {isNotSavedServiceItem &&
                            <>
                                <p className="delete-warning">
                                    This action cannot be undone. All data associated with this service
                                    item will be permanently removed:
                                </p>
                                <ul className="delete-list">
                                    <li>Service details and notes</li>
                                    <li>Labor hours</li>
                                    <li>Attached files ({data.attachments?.length || 0} files)</li>
                                </ul>
                            </>
                        }
                    </ModalBody>
                    <ModalButtons>
                        <Button label="Cancel" type={ButtonType.Text} onClick={onCancel} />
                        {isNotSavedServiceItem ?
                            <>
                                <Button
                                    label={"Delete Service Item"}
                                    type={ButtonType.Contained}
                                    theme={ButtonTheme.Light}
                                    iconUrl={ImageUrls.Trash}
                                    onClick={() => onConfirm(data)}
                                    iconPosition="left"
                                />
                            </>
                            :
                            <>
                                <Button
                                    label={"Decline Service Item"}
                                    type={ButtonType.Contained}
                                    theme={ButtonTheme.Light}
                                    onClick={() => onConfirm(data)}
                                    iconPosition="left"
                                />
                            </>
                        }
                    </ModalButtons>
                </div>
            </ModalContent>
        </Modal>
    )
}
